import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PIXEL_COLORS } from "@/lib/motion";

const PIXELS = Array.from({ length: 28 });

export default function Preloader({ onDone }) {
  const [pct, setPct] = useState(0);
  const [phase, setPhase] = useState("load"); // load -> shutter -> done

  useEffect(() => {
    const DURATION = 2200; // ms to reach 100%
    const start = performance.now();
    let raf;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      setPct(100);
      setTimeout(() => setPhase("shutter"), 250);
      setTimeout(() => { setPhase("done"); onDone && onDone(); }, 1050);
    };
    const tick = (now) => {
      const p = Math.min(1, (now - start) / DURATION);
      setPct(Math.round(p * 100));
      if (p >= 1) { finish(); return; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    // Hard safety cap in case rAF is throttled (background tabs)
    const cap = setTimeout(finish, 3600);
    return () => { cancelAnimationFrame(raf); clearTimeout(cap); };
  }, [onDone]);

  return (
    <AnimatePresence>
      {phase !== "done" && (
        <motion.div
          data-testid="preloader"
          className="fixed inset-0 z-[100] bg-[#030305] flex items-center justify-center overflow-hidden"
          exit={{ opacity: 0 }}
        >
          {/* Camera shutter blades */}
          <motion.div
            className="absolute inset-0 z-[2] bg-black origin-top"
            initial={{ scaleY: 0 }}
            animate={{ scaleY: phase === "shutter" ? 1 : 0 }}
            transition={{ duration: 0.5, ease: [0.83, 0, 0.17, 1] }}
            style={{ display: phase === "shutter" ? "block" : "none" }}
          />
          <div className="relative flex flex-col items-center gap-8">
            {/* Dot -> pixel burst -> logo */}
            <div className="relative h-24 w-64 flex items-center justify-center">
              <motion.div
                className="absolute h-3 w-3 rounded-full"
                style={{ background: "#f59e0b", boxShadow: "0 0 30px #f59e0b" }}
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.4, 0], opacity: [0, 1, 0] }}
                transition={{ duration: 0.7 }}
              />
              {PIXELS.map((_, i) => {
                const angle = (i / PIXELS.length) * Math.PI * 2;
                const radius = 70 + (i % 4) * 12;
                return (
                  <motion.span
                    key={i}
                    className="absolute rounded-[2px]"
                    style={{
                      width: 8 - (i % 3) * 2,
                      height: 8 - (i % 3) * 2,
                      background: PIXEL_COLORS[i % PIXEL_COLORS.length],
                    }}
                    initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                    animate={{
                      x: [0, Math.cos(angle) * radius, Math.cos(angle) * 18],
                      y: [0, Math.sin(angle) * radius, Math.sin(angle) * 10],
                      opacity: [0, 1, 0.9],
                      scale: [0, 1.2, 1],
                    }}
                    transition={{ duration: 1.1, delay: 0.35 + i * 0.012, ease: "easeOut" }}
                  />
                );
              })}
              <motion.img
                src="/brand/logo-dark.png"
                alt="PixelPapad"
                className="relative z-[1] h-16 md:h-20 object-contain"
                initial={{ opacity: 0, scale: 0.9, filter: "blur(8px)" }}
                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                transition={{ duration: 0.6, delay: 0.9 }}
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="h-[2px] w-40 md:w-64 bg-white/10 overflow-hidden rounded-full">
                <motion.div className="h-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#f59e0b,#ec4899,#8b5cf6,#06b6d4)" }} />
              </div>
              <span data-testid="preloader-pct" className="font-display text-white/70 text-sm tabular-nums w-10">{pct}%</span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
